import React, { useEffect, useRef, useState } from 'react';
import api from '../../../shared/api/axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../shared/contexts/AuthContext';
import SidebarMyProfile from '../../sidebars/SidebarMyProfile';
import type { CSSProperties } from 'react';
import ChoshiiLogo from "../../ChoshiiLogo";
import { uploadToR2Direct } from '../../../shared/utils/uploadToR2Direct';
import { useTranslation } from 'react-i18next';
import Modal from '../../settings/Modal';

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const normalizeMe = (u: any) => {
  // picture: keep absolute, or prefix baseUrl for relative
  const rawPicture = u?.picture ?? u?.pictureUrl ?? u?.avatar ?? u?.photoUrl ?? u?.image ?? '';
  const picture =
    typeof rawPicture === 'string' && rawPicture.length > 0
      ? (rawPicture.startsWith('http') ? rawPicture : `${baseUrl}${rawPicture}`)
      : '';

  return {
    profilename: u?.profilename ?? u?.displayName ?? u?.username ?? u?.name ?? '',
    location: u?.location ?? u?.city ?? '',
    bio: u?.bio ?? u?.about ?? u?.description ?? '',
    picture,
  };
};


const Profile = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState('');
  const [modalMessage, setModalMessage] = useState('');

  const [profilename, setProfilename] = useState('');
  const [location, setLocation] = useState('');
  const [bio, setBio] = useState('');
  const [picture, setPicture] = useState('');
  const [isModified, setIsModified] = useState(false);
  const [original, setOriginal] = useState({
    profilename: '',
    location: '',
    bio: '',
    picture: '',
  });

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const imgSrc =
    previewUrl ||
    (picture ? (picture.startsWith('http') ? picture : `${baseUrl}${picture}`) : '');

  // Load profile
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/users/me');
        const n = normalizeMe(data);
        setProfilename(n.profilename);
        setLocation(n.location);
        setBio(n.bio);
        setPicture(n.picture);
        setOriginal({
          profilename: n.profilename,
          location: n.location,
          bio: n.bio,
          picture: n.picture,
        });
        
      } catch (err) {
        console.error('Profile load failed', err);
        navigate('/');
      }
    })();
  }, [navigate]);

  // Derive dirty state automatically (no manual recalc function)
  useEffect(() => {
    const dirty =
      profilename !== original.profilename ||
      location !== original.location ||
      bio !== original.bio ||
      picture !== original.picture ||
      !!selectedFile;
    setIsModified(dirty);
  }, [profilename, location, bio, picture, selectedFile, original]);

  // Save
  const handleUpdate = async () => {
    if (isSaving) return;
    try {
      setIsSaving(true);
      let pictureUrlToSave: string | undefined;
      let r2KeyToSave: string | undefined;
  
      // 1) Upload avatar directly to R2 (if a new file was chosen)
      if (selectedFile) {
        const rawExt = selectedFile.name.split('.').pop() || 'jpg';
        const ext = rawExt.toLowerCase() || 'jpg';
  
        const uid = user?._id || 'anon';
        const keyName = `avatars/avatar-${uid}-${Date.now()}.${ext}`;
  
        const { url, key } = await uploadToR2Direct(selectedFile, keyName);
        pictureUrlToSave = url;  // public Worker/R2 URL
        r2KeyToSave = key;       // R2 object key
      }
  
      // 2) Broad payload (same as before)
      const payload: any = {
        profilename: (profilename || '').trim(),
        location: (location || '').trim(),
        bio: (bio || '').trim(),
        name: (profilename || '').trim(),
        displayName: (profilename || '').trim(),
        username: (profilename || '').trim(),
        city: (location || '').trim(),
        about: (bio || '').trim(),
        description: (bio || '').trim(),
        profile: {
          location: (location || '').trim(),
          bio: (bio || '').trim(),
        },
      };
  
      // 3) Picture handling: clear vs new avatar
      if (picture === '' && !selectedFile) {
        // remove existing avatar
        payload.pictureUrl = '';
        payload.s3Key = '';   // still triggers clear
        payload.r2Key = '';   // explicit clear for new world
      } else if (pictureUrlToSave) {
        // new R2 avatar
        payload.pictureUrl = pictureUrlToSave;
        payload.r2Key = r2KeyToSave;
      }
  
      // 4) Save the profile (unchanged)
      await api.put('/users/me', payload);
  
      // 5) Re-fetch & normalize (unchanged)
      const getRes = await api.get('/users/me');
  
      const n = normalizeMe(getRes.data ?? {});
      const next = {
        profilename: n.profilename || payload.profilename || payload.displayName || '',
        location:   n.location   || payload.location   || payload.city || '',
        bio:        n.bio        || payload.bio        || payload.about || payload.description || '',
        picture:    n.picture    || getRes.data?.pictureUrl || pictureUrlToSave || picture || '',
      };
  
      setUser({ ...getRes.data, ...next });
  
      if (previewUrl) { try { URL.revokeObjectURL(previewUrl); } catch {} }
      setPreviewUrl('');
      setSelectedFile(null);
  
      setProfilename(next.profilename);
      setLocation(next.location);
      setBio(next.bio);
      setPicture(next.picture);
      setOriginal(next);
  
      setModalTitle(t('profile.messages.updated'));
      setModalOpen(true);
    } catch (err: any) {
      console.error(err);  
      if (err?.response?.status === 413) {
  setModalTitle(t('profile.errors.fileTooLarge'));
  setModalMessage(t('profile.errors.fileTooLarge'));
} else {
  setModalTitle(t('profile.errors.updateFailed'));
  setModalMessage(t('profile.errors.updateFailed'));
}
setModalOpen(true);
    } finally {
      setIsSaving(false);
    }
  };
  

  // File change
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  const file = e.target.files?.[0];
  if (!file) return;

  const MAX = 10 * 1024 * 1024;
  if (file.size > MAX) {
    setModalTitle(t('profile.errors.imageTooBig'));
    setModalMessage(t('profile.errors.imageTooBig'));
    setModalOpen(true);
    return;
  }
 
  const fileName = file.name.toLowerCase();
  const hasImageMime = !!file.type && file.type.startsWith('image/');
  const hasImageExtension = /\.(jpg|jpeg|png|gif|webp|bmp|svg|avif|heic|heif)$/i.test(fileName);

  if (!hasImageMime && !hasImageExtension) {
    setModalTitle(t('profile.errors.imageType'));
    setModalMessage(t('profile.errors.imageType'));
    setModalOpen(true);
    return;
  }

  if (previewUrl) {
    try { URL.revokeObjectURL(previewUrl); } catch {}
  }

  const url = URL.createObjectURL(file);
  setPreviewUrl(url);
  setSelectedFile(file);

  if (fileInputRef.current) {
    fileInputRef.current.value = '';
  }
};


  const handleInputChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(e.target.value);
    };

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
      }
    };
  }, [previewUrl]);

  return (
    <div style={styles.page}>
      <SidebarMyProfile/>

      <main style={styles.main}>
        <div style={styles.content}>
          <header style={styles.header}>
            <h2 style={styles.brand}>
              <span style={{ fontWeight: 500 }}>my</span>
               <ChoshiiLogo
                  animateSheen
                  fontFamily="'Playfair Display', serif"
                  style={{ width: '20%', marginBottom: '-22px', marginLeft: '-3%', }}
                />
            </h2>
          
          </header>

          <section style={styles.card}>
            {/* Avatar / Upload */}
            <div style={styles.avatarCol}>
              <div
                style={styles.avatarBox}
                onClick={() => fileInputRef.current?.click()}
              >
                {imgSrc ? (
                  <img src={imgSrc} alt="profile" style={styles.avatarImg} />
                ) : (
                  <div style={styles.avatarPlaceholder}>👻</div>
                )}

                  <span style={styles.avatarOverlay}>{t('profile.avatar.change')}</span>


                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={styles.hiddenFile}
                />
              </div>

              <p style={styles.helpText}>{t('profile.avatar.help')}</p>

              {(previewUrl || picture) && (
                <button
                  type="button"
                  onClick={() => {
                    if (previewUrl) {
                      try { URL.revokeObjectURL(previewUrl); } catch {}
                      setPreviewUrl('');
                      setSelectedFile(null);
                    } else if (picture) {
                      setPicture('');
                    }
                  }}
                  style={styles.removeLink}
                >
                  {previewUrl
                     ? t('profile.avatar.clearSelection')       // keep the ✖︎ in the translation
                     : t('profile.avatar.removePicture')}       // keep the 👻 in the translation
               </button>
              )}
            </div>

            {/* Form fields */}
            <div style={styles.formCol}>
              <div style={styles.field}>
                <label style={styles.label}>
                  <strong>{t('profile.fields.profilenameLabel')}</strong>
                </label>
                <input
                  value={profilename}
                  readOnly
                  disabled
                  style={styles.input}
                  placeholder={t('profile.fields.profilenamePlaceholder')}
                />
              </div>

              <div style={styles.field}>
              <label style={styles.label}>{t('profile.fields.locationLabel')}</label>
                <input
                  value={location}
                  onChange={handleInputChange(setLocation)}
                  style={styles.input}
                  placeholder={t('profile.fields.locationPlaceholder')}

                />
              </div>

              <div style={styles.field1}>
                <label style={styles.label}>
                {t('profile.fields.bioLabel')}
                </label>
                <textarea
                  value={bio}
                  onChange={handleInputChange(setBio)}
                  style={{ ...styles.input1, ...styles.textarea1 }}
                  placeholder={t('profile.fields.bioPlaceholder')}
                  rows={3}
                />
              </div>

              <div style={styles.actions}>
                <button
                  onClick={handleUpdate}
                  style={{
                    ...styles.saveBtn,
                    ...(isModified && !isSaving ? {} : styles.saveBtnDisabled),
                  }}
                  disabled={!isModified || isSaving}
                >
                  {isSaving ? t('profile.actions.saving') : t('profile.actions.save')}
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>

      <Modal
  open={modalOpen}
  onClose={() => setModalOpen(false)}
  title={modalTitle}
  footer={
    <button
      onClick={() => setModalOpen(false)}
      style={styles.modalBtn}
    >
      OK
    </button>
  }
>
  <p style={{ margin: 0 }}>{modalMessage}</p>
</Modal>
    </div>
  );
};

const styles: Record<string, CSSProperties> = {
  page: { display: 'flex', minHeight: '100vh', background: '#0b0d12' },
  main: { flex: 1, padding: '32px', marginTop: '4%', marginLeft: '14%' },
  content: { maxWidth: 960, margin: '0 auto' },

  header: { marginBottom: 20 },
  brand: { fontSize: 28, color: '#e6e9f3', margin: 0 },
  subtext: { color: '#a6adbb', marginTop: 8 },

  card: {
    display: 'grid',
    gridTemplateColumns: '240px 1fr',
    gap: '24px',
    background: '#0f1115',
    border: '1px solid #1f2330',
    borderRadius: 16,
    padding: 24,
    boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
  },

  avatarCol: { textAlign: 'center' },
  avatarBox: {
    position: 'relative',
    width: 180,
    height: 180,
    margin: '8px auto 0',
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid rgb(38, 29, 162)',
    background: 'rgb(38, 29, 162)',
    cursor: 'pointer',
  },
  avatarImg: { width: '100%', height: '100%', objectFit: 'cover' },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 56,
    color: '#e6e9f3',
    background: 'linear-gradient(180deg,rgb(38, 29, 162))',
  },
  avatarOverlay: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 13,
    letterSpacing: '0.3px',
    padding: '8px 0',
    background: 'rgba(10,12,18,0.7)',
    color: '#c8d0e0',
    borderTop: '1px solid rgba(255,255,255,0.06)',
  },
  hiddenFile: { display: 'none' },
  helpText: { fontSize: 12, color: '#7f8796', marginTop: 10 },

  removeLink: {
    marginTop: 6,
    background: 'none',
    border: 'none',
    color: '#c5d0ff',
    cursor: 'pointer',
    fontSize: 13,
    textDecoration: 'underline',
  },

  formCol: { display: 'grid', gap: '16px' },
  field: { display: 'grid', gap: '8px' },
  field1: { display: 'grid', gap: '8px' },
  input1: {
    height: 42,
    padding: '0 12px',
    borderRadius: 10,
    border: '1px solid #2a2f3e',
    background: '#0b0f1a',
    color: '#e6e9f3',
    outline: 'none',
  },
  textarea1: {
    height: 55,
    resize: 'vertical',
    padding: '10px 12px',
    lineHeight: '1.4',
  },
  inputTall: { height: 60, padding: '10px 12px' },
  label: { color: '#cfd6e6', fontSize: 13 },
  input: {
    height: 42,
    padding: '0 12px',
    borderRadius: 10,
    border: '1px solid #2a2f3e',
    background: '#0b0f1a',
    color: '#e6e9f3',
    outline: 'none',
  },
  
  modalBtn: {
  padding: '10px 18px',
  borderRadius: 10,
  background: 'linear-gradient(135deg, #5a2eff, #7a5cff)',
  color: '#fff',
  border: 'none',
  cursor: 'pointer',
  fontWeight: 600,
},

  actions: { marginTop: 8, display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  saveBtn: {
    padding: '10px 18px',
    borderRadius: 10,
    background: 'linear-gradient(135deg, #5a2eff, #7a5cff)',
    color: 'white',
    border: 'none',
    cursor: 'pointer',
    fontWeight: 600,
    letterSpacing: '0.3px',
  },
  saveBtnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
};



export default Profile;
